import { alertsAccessGuard } from '@/router/guards/alertsAccessGuard';
import i18n from '@/i18n';
import { ACCESS_KEYS } from '@/config/entitlementKeys';
import { useDashboardViewState } from '@/features/custom-dashboards/composables/useDashboardViewState';

export default [
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.dashboard_builder'),
      requiresAuth: true,
      // Mirrors the backend custom-assets gate (tool.dashboard-builder.use);
      // frontend gating is UX only — the backend re-checks every call.
      requiresEntitlement: ACCESS_KEYS.TOOL_DASHBOARD_BUILDER,
      requiresFeatureFlag: 'dashboard-builder',
      hideNewsletter: true,
      hideFooter: true,
      breadcrumbs: [
        { label: i18n.global.t('my_dashboards.title'), to: '/my-environment/dashboards' },
        { label: i18n.global.t('page_titles.dashboard_builder'), to: '/builder/dashboards' },
      ],
    },
    path: '/builder/dashboards',
    name: 'dashboard-builder',
    component: () => import('../../features/custom-dashboards/pages/DashboardBuilder.vue'),
  },
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.visualization_marketplace'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_DASHBOARD_BUILDER,
      requiresFeatureFlag: 'dashboard-builder',
      hideNewsletter: true,
      hideFooter: true,
      // The marketplace owns the page-level search field; the chrome's global
      // search next to it just reads as two competing search boxes.
      hideGlobalSearch: true,
      breadcrumbs: [
        { label: i18n.global.t('my_dashboards.title'), to: '/my-environment/dashboards' },
        { label: i18n.global.t('page_titles.dashboard_builder'), to: '/builder/dashboards' },
        {
          label: i18n.global.t('page_titles.visualization_marketplace'),
          to: '/builder/visualizations',
        },
      ],
    },
    path: '/builder/visualizations',
    name: 'visualization-marketplace',
    component: () =>
      import('../../features/custom-dashboards/pages/VisualizationMarketplace.vue'),
  },
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.dashboard_view'),
      requiresAuth: true,
      requiresEntitlement: ACCESS_KEYS.TOOL_DASHBOARD_BUILDER,
      requiresFeatureFlag: 'dashboard-builder',
      hideNewsletter: true,
      hideFooter: true,
      favoritable: (route) => ({
        kind: 'custom_dashboard',
        id: `custom_dashboard:${route.params.id}`,
        label:
          useDashboardViewState().currentDashboardViewTitle.value ||
          i18n.global.t('page_titles.dashboard_view'),
      }),
      breadcrumbs: (route, t) => [
        { label: t('my_dashboards.title'), to: '/my-environment/dashboards' },
        {
          label:
            useDashboardViewState().currentDashboardViewTitle.value ||
            t('page_titles.dashboard_view'),
          to: `/dashboards/${route.params.id}`,
        },
      ],
    },
    path: '/dashboards/:id',
    name: 'dashboard-view',
    component: () => import('../../features/custom-dashboards/pages/DashboardView.vue'),
  },
  {
    meta: {
      cauldron: 'personalisation',
      title: i18n.global.t('page_titles.dataset_browser'),
      requiresAuth: true,
      // No tool entitlement: browsing the catalog is open to any authenticated
      // user (needed so "Create alert" can route here). Access scoping is the
      // data's job — the catalog only surfaces datasets the user may see.
      hideNewsletter: true,
      hideFooter: true,
      breadcrumbs: [{ label: i18n.global.t('menu_dataset_browser'), to: '/builder/datasets' }],
    },
    path: '/builder/datasets',
    name: 'dataset-browser',
    // `?mode=alert` is the first step of "create alert": it loads the alert
    // target registry from /v1/alerts, so it needs the same access as the
    // alert pages. Every other mode stays open to any authenticated user.
    // NOTE: vue-router does not re-run `beforeEnter` when only the query changes,
    // so switching an already-open browser to `?mode=alert` is not re-checked
    // here; every link into alert mode is a fresh navigation from another route.
    beforeEnter: (to) => (to.query?.mode === 'alert' ? alertsAccessGuard(to) : true),
    component: () => import('../../features/datasets/pages/DatasetBrowser.vue'),
    props: (route) => ({
      mode: route.query.mode || 'browse',
      returnRoute: route.query.returnRoute || null,
      shapeFilter: route.query.shapeFilter || null,
      initialSelectedSeries: route.query.selectedSeries
        ? JSON.parse(route.query.selectedSeries)
        : [],
    }),
  },
];
